import type {
  AdicionalCalculo,
  BaseCalculo,
  BasesCalculo,
  CostoFormData,
  TipoBonificacion,
  TipoMoneda,
} from '@/types/costo';

const formatoPorcentaje = new Intl.NumberFormat('es-AR', { maximumFractionDigits: 4 });

const formatoMoneda = (valor: number, moneda: TipoMoneda): string =>
  new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: moneda,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(valor);

/** Bases de un presupuesto sin datos: ambas en ARS y sin importe. */
export const BASES_CALCULO_VACIAS: BasesCalculo = {
  base_imponible: { importe: null, moneda: 'ARS' },
  monto_venta: { importe: null, moneda: 'ARS' },
};

export const ETIQUETA_BASE_CALCULO: Record<BaseCalculo, string> = {
  base_imponible: 'base imponible',
  monto_venta: 'monto de venta',
};

/**
 * Describe el cálculo de un ítem porcentual, por ejemplo
 * "1,5% s/ base imponible + $ 135.000,00" o "21% del 2% s/ monto de venta".
 * La suma fija se muestra en su moneda (por defecto, la del ítem).
 */
export const describirCalculo = (
  porcentaje: number | null | undefined,
  sumaFija?: number | null,
  porcentajeAdicional?: number | null,
  moneda: TipoMoneda = 'ARS',
  baseCalculo: BaseCalculo = 'base_imponible',
  monedaSumaFija?: TipoMoneda | null
): string => {
  const base = `${formatoPorcentaje.format(porcentaje ?? 0)}% s/ ${ETIQUETA_BASE_CALCULO[baseCalculo]}`;

  if (porcentajeAdicional !== null && porcentajeAdicional !== undefined) {
    return `${formatoPorcentaje.format(porcentajeAdicional)}% del ${base}`;
  }

  return sumaFija ? `${base} + ${formatoMoneda(sumaFija, monedaSumaFija ?? moneda)}` : base;
};

/**
 * Monto de un ítem porcentual, igual que lo calcula el backend: con porcentaje
 * adicional se aplica al resultado; si no, se suma la suma fija.
 */
export const calcularMontoPorcentual = (
  base: number | null,
  porcentaje: number,
  sumaFija: number,
  porcentajeAdicional: number | null
): number => {
  const variable = (base ?? 0) * porcentaje / 100;
  const monto = porcentajeAdicional === null ? variable + sumaFija : variable * porcentajeAdicional / 100;

  return Math.round(monto * 100) / 100;
};

/**
 * Convierte un importe entre ARS y USD con la cotización del dólar (ARS por
 * USD), igual que el backend. Devuelve null si hace falta convertir y no hay
 * cotización.
 */
export const convertirImporte = (
  importe: number,
  de: TipoMoneda,
  a: TipoMoneda,
  cotizacion: number | null | undefined
): number | null => {
  if (de === a) return importe;
  if (!cotizacion || cotizacion <= 0) return null;

  return de === 'USD' ? importe * cotizacion : importe / cotizacion;
};

/**
 * Moneda e importe neto con los que el ítem sale en el PDF, igual que el
 * backend: bruto y bonificación se convierten por separado a la moneda de
 * impresión. Sin cotización sale en su propia moneda.
 */
export const importeImpreso = (
  item: {
    monto: number;
    montoBruto?: number;
    bonificacionImporte?: number;
    moneda: TipoMoneda;
    monedaImpresion?: TipoMoneda | null;
  },
  cotizacion: number | null | undefined
): { moneda: TipoMoneda; importe: number } => {
  const moneda = item.monedaImpresion ?? item.moneda;
  const bruto = convertirImporte(item.montoBruto ?? item.monto, item.moneda, moneda, cotizacion);

  if (bruto === null) return { moneda: item.moneda, importe: item.monto };

  const redondear = (valor: number) => Math.round(valor * 100) / 100;
  const bonificacion = convertirImporte(item.bonificacionImporte ?? 0, item.moneda, moneda, cotizacion) ?? 0;

  return { moneda, importe: redondear(redondear(bruto) - redondear(bonificacion)) };
};

/** Importe de la base de cálculo en la moneda del ítem; null si no se puede calcular. */
export const baseEnMoneda = (
  bases: BasesCalculo,
  baseCalculo: BaseCalculo,
  moneda: TipoMoneda,
  cotizacion: number | null | undefined
): number | null => {
  const { importe, moneda: monedaBase } = bases[baseCalculo];

  return importe === null ? null : convertirImporte(importe, monedaBase, moneda, cotizacion);
};

/**
 * ¿El ítem porcentual tiene la base o la suma fija en otra moneda y falta la
 * cotización para convertirla? Mientras tanto, lo que está en otra moneda no
 * cuenta.
 */
export const faltaCotizacion = (
  item: {
    modoCalculo?: string;
    baseCalculo?: BaseCalculo;
    moneda: TipoMoneda;
    porcentajeAdicional?: number | null;
    sumaFija?: number | null;
    sumaFijaMoneda?: TipoMoneda | null;
  },
  bases: BasesCalculo,
  cotizacion: number | null | undefined
): boolean => {
  if (item.modoCalculo !== 'porcentaje' || cotizacion) return false;

  const sumaFijaEnOtraMoneda =
    item.porcentajeAdicional == null && !!item.sumaFija && (item.sumaFijaMoneda ?? item.moneda) !== item.moneda;

  return bases[item.baseCalculo ?? 'base_imponible'].moneda !== item.moneda || sumaFijaEnOtraMoneda;
};

type CalculoPorcentual = Required<
  Pick<CostoFormData, 'modo_calculo' | 'porcentaje' | 'porcentaje_adicional' | 'suma_fija' | 'suma_fija_moneda'>
>;

/**
 * Valida los campos de un ítem porcentual (como texto de los inputs) y arma los
 * datos para la API. Suma fija y porcentaje adicional son excluyentes.
 */
export const armarCalculoPorcentual = (campos: {
  porcentaje: string;
  adicional: AdicionalCalculo;
  sumaFija: string;
  monedaSumaFija: TipoMoneda;
  porcentajeAdicional: string;
}): { error: string } | { datos: CalculoPorcentual } => {
  const porcentaje = parseFloat(campos.porcentaje);
  if (isNaN(porcentaje) || porcentaje < 0 || porcentaje > 100) {
    return { error: 'El porcentaje debe estar entre 0 y 100' };
  }

  if (campos.adicional === 'porcentaje') {
    const porcentajeAdicional = parseFloat(campos.porcentajeAdicional);
    if (isNaN(porcentajeAdicional) || porcentajeAdicional < 0 || porcentajeAdicional > 100) {
      return { error: 'El porcentaje sobre el resultado debe estar entre 0 y 100' };
    }

    return {
      datos: {
        modo_calculo: 'porcentaje',
        porcentaje,
        porcentaje_adicional: porcentajeAdicional,
        suma_fija: null,
        suma_fija_moneda: null,
      },
    };
  }

  const sumaFija = campos.sumaFija.trim() === '' ? 0 : parseFloat(campos.sumaFija);
  if (isNaN(sumaFija) || sumaFija < 0) {
    return { error: 'La suma fija no puede ser negativa' };
  }

  return {
    datos: {
      modo_calculo: 'porcentaje',
      porcentaje,
      porcentaje_adicional: null,
      suma_fija: sumaFija,
      suma_fija_moneda: campos.monedaSumaFija,
    },
  };
};

/**
 * Importe que se descuenta del bruto, igual que lo calcula el backend: porcentaje
 * del bruto o monto fijo, con tope en el bruto.
 */
export const calcularBonificacion = (
  bruto: number,
  tipo: TipoBonificacion | null | undefined,
  valor: number | null | undefined
): number => {
  if (!tipo || valor === null || valor === undefined) return 0;

  const importe = tipo === 'porcentaje' ? Math.round(bruto * valor) / 100 : Math.round(valor * 100) / 100;

  return Math.min(importe, bruto);
};

/** Describe una bonificación, por ejemplo "Bonif. 20%" o "Bonif. US$ 150,00". */
export const describirBonificacion = (
  tipo: TipoBonificacion,
  valor: number,
  moneda: TipoMoneda = 'ARS'
): string => {
  if (tipo === 'porcentaje') return `Bonif. ${formatoPorcentaje.format(valor)}%`;

  return `Bonif. ${formatoMoneda(valor, moneda)}`;
};

type DatosBonificacion = Required<Pick<CostoFormData, 'bonificacion_tipo' | 'bonificacion_valor'>>;

const tieneHastaDecimales = (valor: number, decimales: number): boolean => {
  const escala = 10 ** decimales;
  return Math.abs(valor * escala - Math.round(valor * escala)) < 1e-6;
};

/**
 * Valida la bonificación y arma los datos para la API. Un monto fijo puede
 * superar el monto del ítem (se bonifica antes de cargarlo): el neto queda en cero.
 */
export const armarBonificacion = (
  tipo: TipoBonificacion,
  valor: number | null | undefined
): { error: string } | { datos: DatosBonificacion } => {
  if (valor === null || valor === undefined || isNaN(valor) || valor <= 0) {
    return { error: 'La bonificación debe ser mayor a cero' };
  }

  if (tipo === 'porcentaje') {
    if (valor > 100) return { error: 'La bonificación no puede superar el 100%' };
    if (!tieneHastaDecimales(valor, 4)) return { error: 'El porcentaje debe tener máximo 4 decimales' };
  } else if (!tieneHastaDecimales(valor, 2)) {
    return { error: 'La bonificación debe tener máximo 2 decimales' };
  }

  return { datos: { bonificacion_tipo: tipo, bonificacion_valor: valor } };
};
